// AUTO-GENERATED — do not edit. Run `pnpm build` in packages/domain-data.
// Sources:
//   business/ishvaram/data/commerce/report_plans.snapshot.json (price/pages/copy)
//   business/ishvaram/data/commerce/report_deliverables.generated.yaml (deliverables mirror)
//   business/ishvaram/data/commerce/product_perks.generated.yaml (perks mirror)

export type PremiumReportPlanId =
  | "brihat_kundali"
  | "career_report"
  | "marriage_report"
  | "annual_forecast"
  | "muhurat_report"
  | "dosha_remedy_plan"
  | "sade_sati"
  | "pitra_dosh"
  | "mahadasha_forecast"
  | "ask_one_question";

/** A localized deliverable line. `personalized` carries the render-time
 * {{chart_reason}} placeholder (rule 24/29 — never an LLM literal). */
export interface ReportDeliverableLine {
  en: string;
  hi: string;
}

/** The 3-item "answer-continuation" deliverables surfaced before the single CTA
 * (rule 39/40/43). Authored in Supabase catalog.report_deliverables and mirrored
 * to commerce/report_deliverables.generated.yaml. */
export interface ReportDeliverables {
  sections: ReportDeliverableLine;
  delivery: ReportDeliverableLine;
  personalized: ReportDeliverableLine;
}

/** A "what you get" perk merged onto the plan from Supabase catalog.perks +
 * catalog.product_perks (build mirror: commerce/product_perks.generated.yaml).
 * `is_entitlement` true = the backend GRANTS it (credits, follow-ups);
 * false = a display-only badge rendered on the offer card. */
export interface ReportPerk {
  id: string;
  label: string;
  label_hi: string | null;
  kind: string;
  is_entitlement: boolean;
  icon: string | null;
  value: string | null;
}

export interface PremiumReportPlan {
  id: PremiumReportPlanId;
  name: string;
  name_hi?: string;
  description: string;
  description_hi?: string;
  price_inr: number;
  price_paisa: number;
  pages: number;
  credits_included: number;
  llm_model_override?: string;
  /** Exactly-3 concrete deliverables shown before the CTA (answer-continuation). */
  deliverables: ReportDeliverables;
  /** "What you get" perk badges (may be empty until the perks mirror is generated). */
  perks: ReportPerk[];
  shopify?: {
    handle: string;
    product_id: number;
    variant_id: number;
  };
}

export const premiumReportPlans: readonly PremiumReportPlan[] = [
  {
    id: "brihat_kundali",
    name: "Brihat Kundali Report",
    name_hi: "बृहत् कुंडली रिपोर्ट",
    description:
      "Complete private PDF — personality, career, marriage, health, dasha predictions, yearly forecast, remedies",
    description_hi:
      "संपूर्ण निजी PDF — व्यक्तित्व, जीविका, विवाह, स्वास्थ्य, दशा फल, वार्षिक भविष्यवाणी, उपाय",
    price_inr: 199,
    price_paisa: 19900,
    pages: 51,
    llm_model_override: "claude_premium",
    credits_included: 10,
    shopify: {
      handle: "brihat-kundali-report",
      product_id: 8105748430941,
      variant_id: 44344311414877,
    },
    deliverables: {
      sections: {
        en: "All 12 bhav, mahadasha + antardasha timeline, yogas & doshas",
        hi: "Saari 12 bhav, mahadasha + antardasha timeline, yoga aur dosha",
      },
      delivery: {
        en: "51-page PDF + on-page reading, ready in 2 minutes",
        hi: "51-page PDF + on-page reading, 2 minute mein ready",
      },
      personalized: {
        en: "Upay matched to {{chart_reason}} in your chart",
        hi: "Aapki kundali ke {{chart_reason}} se matched upay",
      },
    },
    perks: [],
  },
  {
    id: "career_report",
    name: "Career & Finance Report",
    name_hi: "जीविका एवं धन रिपोर्ट",
    description:
      "Comprehensive 24-page report — 10th house & D10 career chart, aptitude & fields, Ashtakavarga/Shadbala strength, full dasha timeline, transits, wealth yogas, best periods, remedies, FAQ",
    price_inr: 149,
    price_paisa: 14900,
    pages: 28,
    credits_included: 5,
    shopify: {
      handle: "career-finance-report",
      product_id: 8105748463709,
      variant_id: 44344311447645,
    },
    deliverables: {
      sections: {
        en: "10th house & D10 career chart, wealth yogas, best work windows",
        hi: "10th house aur D10 career chart, dhan yoga, best work windows",
      },
      delivery: {
        en: "28-page PDF + on-page reading, ready in 2 minutes",
        hi: "28-page PDF + on-page reading, 2 minute mein ready",
      },
      personalized: {
        en: "Dasha timeline read for {{chart_reason}} in your chart",
        hi: "Aapki kundali ke {{chart_reason}} ke liye dasha timeline",
      },
    },
    perks: [],
  },
  {
    id: "marriage_report",
    name: "Marriage & Compatibility Report",
    name_hi: "विवाह एवं अनुकूलता रिपोर्ट",
    description:
      "Detailed marriage timing, spouse characteristics, Mangal Dosha analysis, compatibility insights",
    price_inr: 149,
    price_paisa: 14900,
    pages: 10,
    credits_included: 5,
    shopify: {
      handle: "marriage-compatibility-report",
      product_id: 8105748496477,
      variant_id: 44344311513181,
    },
    deliverables: {
      sections: {
        en: "7th house, Mangal Dosh, Kundali Milan, marriage-timing dasha",
        hi: "7th house, Mangal Dosh, Kundali Milan, shaadi-timing dasha",
      },
      delivery: {
        en: "10-page PDF + on-page reading, ready in 2 minutes",
        hi: "10-page PDF + on-page reading, 2 minute mein ready",
      },
      personalized: {
        en: "Compatibility & timing read for {{chart_reason}} in your chart",
        hi: "Aapki kundali ke {{chart_reason}} ka compatibility aur timing read",
      },
    },
    perks: [],
  },
  {
    id: "annual_forecast",
    name: "Annual Forecast 2026-27",
    name_hi: "वार्षिक भविष्यफल 2026-27",
    description:
      "Month-by-month predictions for the coming year based on transits and dashas — with chart, Ashtakavarga/Shadbala strength, dasha timeline & remedies",
    price_inr: 149,
    price_paisa: 14900,
    pages: 17,
    credits_included: 10,
    shopify: {
      handle: "annual-forecast-2026-27",
      product_id: 8105748529245,
      variant_id: 44344311578717,
    },
    deliverables: {
      sections: {
        en: "Month-by-month transits & dasha, caution and decision windows",
        hi: "Month-by-month transit aur dasha, caution aur decision windows",
      },
      delivery: {
        en: "17-page PDF + on-page reading, ready in 2 minutes",
        hi: "17-page PDF + on-page reading, 2 minute mein ready",
      },
      personalized: {
        en: "12-month outlook read for {{chart_reason}} in your chart",
        hi: "Aapki kundali ke {{chart_reason}} ka 12-month outlook",
      },
    },
    perks: [],
  },
  {
    id: "muhurat_report",
    name: "Personal Muhurat & Sankalp Plan",
    name_hi: "व्यक्तिगत मुहूर्त एवं संकल्प योजना",
    description:
      "Purpose-specific timing guidance, avoid windows, sankalp, and ritual checklist — with chart, strength, dasha timeline, transits &amp; remedies",
    price_inr: 149,
    price_paisa: 14900,
    pages: 17,
    credits_included: 5,
    deliverables: {
      sections: {
        en: "Purpose-specific timing, avoid windows, sankalp & ritual checklist",
        hi: "Purpose-specific timing, avoid windows, sankalp aur ritual checklist",
      },
      delivery: {
        en: "17-page PDF + on-page reading, ready in 2 minutes",
        hi: "17-page PDF + on-page reading, 2 minute mein ready",
      },
      personalized: {
        en: "Shubh windows matched to {{chart_reason}} in your chart",
        hi: "Aapki kundali ke {{chart_reason}} se matched shubh windows",
      },
    },
    perks: [],
  },
  {
    id: "dosha_remedy_plan",
    name: "Dosha Diagnosis & Remedy Plan",
    name_hi: "दोष निदान एवं उपाय योजना",
    description:
      "Chart-specific dosha severity, cancellation checks, safe remedy routine, and matched product guidance",
    price_inr: 199,
    price_paisa: 19900,
    pages: 18,
    credits_included: 10,
    deliverables: {
      sections: {
        en: "Dosha severity, cancellation checks, safe upay sequence",
        hi: "Dosha severity, cancellation check, safe upay sequence",
      },
      delivery: {
        en: "18-page PDF + on-page reading, ready in 2 minutes",
        hi: "18-page PDF + on-page reading, 2 minute mein ready",
      },
      personalized: {
        en: "Upay sequence matched to {{chart_reason}} in your chart",
        hi: "Aapki kundali ke {{chart_reason}} se matched upay sequence",
      },
    },
    perks: [],
  },
  {
    id: "sade_sati",
    name: "Sade Sati Personal Report",
    name_hi: "साढ़े साती व्यक्तिगत रिपोर्ट",
    description:
      "Your natal Saturn, current Sade Sati phase & dates, dasha context, plus chart, strength, transits & remedies",
    price_inr: 149,
    price_paisa: 14900,
    pages: 13,
    credits_included: 5,
    deliverables: {
      sections: {
        en: "Sade Sati phase, natal Shani, current dasha, pressure areas",
        hi: "Sade Sati phase, natal Shani, current dasha, pressure areas",
      },
      delivery: {
        en: "13-page PDF + on-page reading, ready in 2 minutes",
        hi: "13-page PDF + on-page reading, 2 minute mein ready",
      },
      personalized: {
        en: "Shani upay matched to {{chart_reason}} in your chart",
        hi: "Aapki kundali ke {{chart_reason}} se matched Shani upay",
      },
    },
    perks: [],
  },
  {
    id: "pitra_dosh",
    name: "Pitra Dosh Personal Report",
    name_hi: "पितृ दोष व्यक्तिगत रिपोर्ट",
    description:
      "Engine-verified Pitra Dosh diagnosis, 9th-house ancestral signature, dasha activation window, and chart-specific upay (tarpan, shradh)",
    price_inr: 149,
    price_paisa: 14900,
    pages: 12,
    credits_included: 5,
    deliverables: {
      sections: {
        en: "9th house, Surya, Rahu/Ketu, cancellation, dasha activation",
        hi: "9th house, Surya, Rahu/Ketu, cancellation, dasha activation",
      },
      delivery: {
        en: "12-page PDF + on-page reading, ready in 2 minutes",
        hi: "12-page PDF + on-page reading, 2 minute mein ready",
      },
      personalized: {
        en: "Ancestral upay matched to {{chart_reason}} in your chart",
        hi: "Aapki kundali ke {{chart_reason}} se matched ancestral upay",
      },
    },
    perks: [],
  },
  {
    id: "mahadasha_forecast",
    name: "12-Month Mahadasha Forecast",
    name_hi: "१२ माह की महादशा भविष्यवाणी",
    description:
      "Current mahadasha/antardasha, the next 12 months of sub-periods, next transition, plus chart, strength, transits & remedies",
    price_inr: 199,
    price_paisa: 19900,
    pages: 16,
    credits_included: 10,
    deliverables: {
      sections: {
        en: "Current mahadasha & antardasha, next transition, month-level timing",
        hi: "Current mahadasha aur antardasha, next transition, month-level timing",
      },
      delivery: {
        en: "16-page PDF + on-page reading, ready in 2 minutes",
        hi: "16-page PDF + on-page reading, 2 minute mein ready",
      },
      personalized: {
        en: "Timing read for {{chart_reason}} in your chart",
        hi: "Aapki kundali ke {{chart_reason}} ka timing read",
      },
    },
    perks: [],
  },
  {
    id: "ask_one_question",
    name: "Ask One Question",
    name_hi: "आपका एक प्रश्न",
    description:
      "Ask one specific question and get a short 1-page reading answered from your exact kundali — the chart pattern behind it and the upay.",
    description_hi:
      "एक विशेष प्रश्न पूछें और अपनी कुंडली से एक-पृष्ठ का उत्तर पाएं — पीछे का ग्रह पैटर्न और उपाय।",
    price_inr: 49,
    price_paisa: 4900,
    pages: 1,
    credits_included: 0,
    deliverables: {
      sections: {
        en: "Your one question, answered from your chart — the planetary cause + the upay",
        hi: "Aapka ek sawaal, kundali se answered — planetary cause + upay",
      },
      delivery: {
        en: "1-page PDF + on-page reading, ready in 2 minutes",
        hi: "1-page PDF + on-page reading, 2 minute mein ready",
      },
      personalized: {
        en: "A direct answer grounded in {{chart_reason}} in your chart",
        hi: "Aapki kundali ke {{chart_reason}} par grounded seedha jawaab",
      },
    },
    perks: [],
  },
] as const;

export const premiumReportPlanIds: readonly PremiumReportPlanId[] = premiumReportPlans.map(
  (plan) => plan.id
);

export const premiumReportPlanMap: Readonly<Record<PremiumReportPlanId, PremiumReportPlan>> =
  Object.fromEntries(premiumReportPlans.map((plan) => [plan.id, plan])) as Record<
    PremiumReportPlanId,
    PremiumReportPlan
  >;

export function getPremiumReportPlan(planId: string): PremiumReportPlan | null {
  return premiumReportPlanMap[planId as PremiumReportPlanId] ?? null;
}
